import React, { useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { ProgressBar } from "primereact/progressbar";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { CLOSURE_STATE_LABELS, CLOSURE_STATE_SEVERITY } from "../StationPerformance.consts";
import { MAX_MONTHS_PER_CALL, planChunks, useBackfill } from "../hooks/useBackfill";
import type { MonthDrift, RunState } from "../hooks/useBackfill";

const toYearMonth = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const fromYearMonth = (yearMonth: string) => {
  const [y, m] = yearMonth.split("-").map(Number);
  return new Date(y, m - 1, 1);
};

const STATUS_SEVERITY: Record<string, "success" | "warning" | "danger" | "info"> = {
  COMPLETE: "success",
  FAILED: "danger",
  RUNNING: "info",
  NEVER_COMPUTED: "warning",
};

/**
 * Recompute the aggregates, and see the state of every month that has one.
 *
 * This screen exists because the nightly job cannot fill in history. It recomputes the OPEN month plus
 * anything still in GRACE and stops at the first SEALED month, by design, so on a fresh deployment
 * exactly one month has figures and everything older reads as never computed. A backfill is the only
 * thing that changes that, and it is also the only thing that can repair a sealed month once a session
 * arrives late.
 *
 * The button is deliberately not a one-click "do everything": the start month has to be chosen, because
 * nothing in the API reports the date of the oldest session, and a wrong guess either misses history or
 * spends time recomputing empty months.
 */
const BackfillPanel: React.FC = () => {
  const { months, drift, checkDrift, setCheckDrift, loading, error, progress, earliestComputed, run, reload } =
    useBackfill();

  const now = new Date();
  const [fromDate, setFromDate] = useState<Date>(new Date(now.getFullYear() - 2, 0, 1));
  const [toDate, setToDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth(), 1));

  const from = toYearMonth(fromDate);
  const to = toYearMonth(toDate);
  const chunks = useMemo(() => planChunks(from, to), [from, to]);
  const rangeInvalid = chunks.length === 0;

  const sealedInRange = useMemo(
    () => (months ?? []).filter((m) => m.closureState === "SEALED" && m.yearMonth >= from && m.yearMonth <= to),
    [months, from, to],
  );

  const confirmRun = () => {
    const sealedNote =
      sealedInRange.length > 0
        ? ` ${sealedInRange.length} of them are sealed, and their figures will be rewritten and re-sealed.`
        : "";
    confirmDialog({
      header: "Recompute aggregates",
      icon: "pi pi-exclamation-triangle",
      message:
        `This recomputes every month from ${from} to ${to} in full, across all stations.` +
        sealedNote +
        ` It runs in ${chunks.length} call${chunks.length === 1 ? "" : "s"} and is safe to repeat.`,
      acceptLabel: "Recompute",
      rejectLabel: "Cancel",
      accept: () => run(from, to),
    });
  };

  const failedChunks = progress.results.filter((r) => r.error);
  const sealedTouched = progress.results.flatMap((r) => r.sealedMonthsAffected);

  return (
    <div className="flex flex-column gap-3">
      <ConfirmDialog />

      <Message
        severity="info"
        className="w-full"
        text="The nightly run only recomputes the open month and anything still in grace. History, and any sealed month that fell behind, can only be filled in from here."
      />

      <Card title="Recompute all stations">
        <div className="flex flex-wrap gap-3 align-items-end">
          <div className="flex flex-column gap-1">
            <label className="text-sm text-color-secondary">From</label>
            <Calendar
              value={fromDate}
              onChange={(e) => e.value && setFromDate(e.value as Date)}
              view="month"
              dateFormat="yy-mm"
              showIcon
              disabled={progress.running}
            />
          </div>
          <div className="flex flex-column gap-1">
            <label className="text-sm text-color-secondary">To</label>
            <Calendar
              value={toDate}
              onChange={(e) => e.value && setToDate(e.value as Date)}
              view="month"
              dateFormat="yy-mm"
              showIcon
              disabled={progress.running}
            />
          </div>
          <Button
            label={progress.running ? "Recomputing…" : "Recompute all months"}
            icon="pi pi-refresh"
            onClick={confirmRun}
            disabled={progress.running || rangeInvalid}
            loading={progress.running}
          />
          {earliestComputed && (
            <Button
              label={`Start from earliest computed (${earliestComputed})`}
              icon="pi pi-angle-double-left"
              text
              onClick={() => setFromDate(fromYearMonth(earliestComputed))}
              disabled={progress.running}
            />
          )}
        </div>

        {rangeInvalid ? (
          <Message severity="warn" className="mt-3 w-full" text="'From' must not be after 'To'." />
        ) : (
          <div className="text-color-secondary text-sm mt-3">
            {chunks.length} call{chunks.length === 1 ? "" : "s"}, at most {MAX_MONTHS_PER_CALL} months each.
            {sealedInRange.length > 0 && ` ${sealedInRange.length} sealed month(s) in range will be rewritten.`}
          </div>
        )}

        {/* Nothing here reports the oldest session, so the caller has to pick. Saying so is better than
            a default that looks derived from the data when it is not. */}
        <div className="text-color-secondary text-sm mt-2">
          Recomputing a month with no sessions is harmless and fast, so starting earlier than necessary
          costs little. Starting too late silently leaves history missing.
        </div>
      </Card>

      {(progress.running || progress.results.length > 0) && (
        <Card title="Progress">
          <ProgressBar
            value={progress.chunksTotal === 0 ? 0 : Math.round((progress.chunksDone / progress.chunksTotal) * 100)}
          />
          <div className="text-sm mt-2">
            {progress.chunksDone} of {progress.chunksTotal} call(s) · {progress.monthsProcessed} month(s) recomputed
          </div>

          {failedChunks.length > 0 && (
            <Message
              severity="error"
              className="mt-3 w-full"
              text={`${failedChunks.length} call(s) failed: ${failedChunks
                .map((f) => `${f.from}..${f.to} (${f.error})`)
                .join("; ")}`}
            />
          )}

          {!progress.running && failedChunks.length === 0 && progress.results.length > 0 && (
            <Message
              severity="success"
              className="mt-3 w-full"
              text={
                `Recomputed ${progress.monthsProcessed} month(s).` +
                (sealedTouched.length > 0 ? ` ${sealedTouched.length} sealed month(s) were rewritten and re-sealed.` : "")
              }
            />
          )}
        </Card>
      )}

      <Card>
        <div className="flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
          <span className="font-bold">Month by month</span>
          <div className="flex gap-3 align-items-center">
            <span
              className="text-sm text-color-secondary"
              title="Compares stored session counts against the source. The only way a sealed month that fell behind becomes visible."
            >
              Check for drift
            </span>
            <InputSwitch checked={checkDrift} onChange={(e) => setCheckDrift(!!e.value)} disabled={progress.running} />
            <Button label="Refresh" icon="pi pi-refresh" outlined size="small" onClick={reload} loading={loading} />
          </div>
        </div>

        {error && <Message severity="error" className="w-full mb-3" text={error} />}

        {drift.length > 0 && (
          <Message
            severity="warn"
            className="w-full mb-3"
            text={`${drift.length} month(s) have fallen behind the source: ${drift
              .map((d: MonthDrift) => `${d.yearMonth} (${d.missingFromAggregate} missing${d.selfHealing ? ", self-healing" : ", needs backfill"})`)
              .join("; ")}`}
          />
        )}

        {loading && !months ? (
          <div className="flex justify-content-center p-4">
            <ProgressSpinner style={{ width: 40, height: 40 }} />
          </div>
        ) : (
          <DataTable
            value={months ?? []}
            dataKey="yearMonth"
            paginator
            rows={25}
            size="small"
            emptyMessage="No month has been computed yet. Recompute a range above to populate this."
            scrollable
          >
            <Column field="yearMonth" header="Month" sortable frozen style={{ minWidth: "7rem" }} />
            <Column
              header="Run"
              style={{ minWidth: "8rem" }}
              body={(r: RunState) => (
                <Tag value={r.status} severity={STATUS_SEVERITY[r.status] ?? "info"} />
              )}
            />
            <Column
              header="Closure"
              style={{ minWidth: "8rem" }}
              body={(r: RunState) => (
                <Tag
                  value={r.closureState}
                  severity={CLOSURE_STATE_SEVERITY[r.closureState] ?? "info"}
                  title={CLOSURE_STATE_LABELS[r.closureState]}
                />
              )}
            />
            <Column field="sessionsRead" header="Sessions" sortable style={{ minWidth: "7rem" }} />
            <Column field="unitsWritten" header="Units" sortable style={{ minWidth: "6rem" }} />
            <Column
              header="Rejected"
              style={{ minWidth: "8rem" }}
              body={(r: RunState) => {
                const total = r.rejectedBlankStation + r.rejectedBlankEvse;
                if (total === 0) return <span className="text-color-secondary">0</span>;
                return (
                  <span
                    className="text-orange-600"
                    title={`${r.rejectedBlankStation} with no station, ${r.rejectedBlankEvse} with no connector. A rising count is an upstream data problem.`}
                  >
                    {total}
                  </span>
                );
              }}
            />
            <Column
              header="Cost fallback"
              style={{ minWidth: "8rem" }}
              body={(r: RunState) =>
                r.costFallbackApplied === 0 ? (
                  <span className="text-color-secondary">0</span>
                ) : (
                  <span
                    className="text-orange-600"
                    title="Sessions whose totalCost was zero while their components were populated, so money was recovered from the components."
                  >
                    {r.costFallbackApplied}
                  </span>
                )
              }
            />
            <Column
              header="Rewrites"
              style={{ minWidth: "6rem" }}
              body={(r: RunState) =>
                r.resealCount === 0 ? (
                  <span className="text-color-secondary">0</span>
                ) : (
                  <span title="Times this sealed month has been recomputed and re-sealed.">{r.resealCount}</span>
                )
              }
            />
            <Column
              header="Computed"
              style={{ minWidth: "11rem" }}
              body={(r: RunState) => (
                <span className="text-sm">
                  {r.computedAt ? new Date(r.computedAt).toLocaleString() : "—"}
                  {r.durationMs > 0 && (
                    <span className="text-color-secondary"> · {(r.durationMs / 1000).toFixed(1)}s</span>
                  )}
                </span>
              )}
            />
            <Column
              header="Error"
              style={{ minWidth: "12rem" }}
              body={(r: RunState) =>
                r.lastError ? (
                  <span className="text-red-600 text-sm" title={r.lastError}>
                    {r.lastError.slice(0, 60)}
                  </span>
                ) : (
                  <span className="text-color-secondary">—</span>
                )
              }
            />
          </DataTable>
        )}
      </Card>
    </div>
  );
};

export default BackfillPanel;
